/**
 * useForgeQueries.js
 *
 * Central registry for every TanStack Query hook in the app.
 *
 * ── Why one file? ───────────────────────────────────────────
 *  • Query keys are co-located with fetchers — no typo bugs.
 *  • PrefetchLink imports keys from here, so cache invalidation
 *    and prefetch always target the exact same key.
 *  • Switching the fetcher (e.g., add auth headers) happens in
 *    one place, not across 12 components.
 */

import { useQuery } from '@tanstack/react-query';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/* ── Shared fetcher (throws on non-OK so React Query sees the error) ── */
const get = async (path) => {
  const res = await fetch(`${API}${path}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} — ${path}`);
  return res.json();
};

/* ════════════════════════════════════════════════════════════
   QUERY KEYS
   Export these so PrefetchLink and cache invalidation always
   use the identical key — no string duplication anywhere.
════════════════════════════════════════════════════════════ */
export const KEYS = {
  allProducts:   ['allproducts'],
  product:       (id) => ['allproducts', 'product', Number(id)],
  newCollections:['newcollections'],
  packs:         ['packs'],
  featured:      ['featured'],
  comingSoon:    ['comingsoon'],
  reviews:       (productId) => ['reviews', Number(productId)],
};

/* ════════════════════════════════════════════════════════════
   HOOKS
════════════════════════════════════════════════════════════ */

/**
 * All products — used by ShopCategory, ShopContext, Product page.
 *
 * Before: useEffect → fetch('/allproducts') → setProducts
 * After:  one line, cached for 5 min, background refresh is automatic.
 */
export const useAllProducts = (options = {}) =>
  useQuery({
    queryKey:   KEYS.allProducts,
    queryFn:    () => get('/allproducts'),
    retry:      6,
    retryDelay: 5000,
    ...options,
  });

/**
 * Single product — derived from the allProducts cache.
 * Uses `select` to slice one item from the already-cached list, so
 * navigating to /product/1 costs 0 network requests if the list is warm.
 *
 * IMPORTANT: this must share the same queryKey as useAllProducts /
 * ShopContext (KEYS.allProducts) — using KEYS.product(id) here would put
 * it in a separate cache slot and defeat the whole point of `select`,
 * forcing a fresh /allproducts fetch on every product page visit.
 */
export const useProduct = (productId, options = {}) =>
  useQuery({
    queryKey: KEYS.allProducts,
    queryFn:  () => get('/allproducts'),
    select:   (data) => data.find((p) => p.id === Number(productId)) ?? null,
    enabled:  !!productId,
    ...options,
  });

/** New collections strip (homepage) */
export const useNewCollections = (options = {}) =>
  useQuery({
    queryKey: KEYS.newCollections,
    queryFn:  () => get('/newcollections'),
    ...options,
  });

/** Pack pricing (homepage hero, checkout) */
export const usePacks = (options = {}) =>
  useQuery({
    queryKey: KEYS.packs,
    queryFn:  () => get('/packs'),
    ...options,
  });

/** Featured product */
export const useFeatured = (options = {}) =>
  useQuery({
    queryKey: KEYS.featured,
    queryFn:  () => get('/featured'),
    ...options,
  });

/** Coming soon products */
export const useComingSoon = (options = {}) =>
  useQuery({
    queryKey: KEYS.comingSoon,
    queryFn:  () => get('/comingsoon'),
    ...options,
  });

/** Product reviews */
export const useReviews = (productId, options = {}) =>
  useQuery({
    queryKey: KEYS.reviews(productId),
    queryFn:  () => get(`/reviews/${productId}`),
    enabled:  !!productId,
    ...options,
  });
